import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import Icon from '@material-ui/core/Icon';
import DatePicker from '@components/DatePicker';
import { apiClient } from '@services/api';

/**
 * Liberação de cupos (fase 1, 2026-06-05)
 *
 * Bola flutuante (canto inferior direito, todas as telas do admin):
 * - Consultor: pesquisa tour → data → vê liberadas/usadas/livres → monta carrinho
 *   (editar/excluir) → "Pedir liberação" → número LB. Bolinha colorida pelo
 *   resultado do último pedido revisado: verde (aprovado) / vermelho (recusado) /
 *   amarelo (parcial).
 * - Logística/admin/gerência (administrador|gerente|logistica): aba Aprovações com
 *   bolinha AMARELA quando há pedidos pendentes; quantidade editável; aprovar
 *   individual ou todos; recusar SOMENTE individual; comentário de devolução.
 */

const C = {
  bg:      '#0f1e35',
  card:    '#172B4D',
  inner:   '#0f2040',
  accent:  '#62DEC2',
  textSec: '#8ba5c4',
  textMain:'#e8f0fb',
  border:  '#1e3a5f',
  danger:  '#ef4444',
  warn:    '#facc15',
  ok:      '#4ade80',
};

const APPROVER_PERMISSIONS = ['administrador', 'gerente', 'logistica'];
const POLL_INTERVAL = 60000; // 60s

const statusColor = (status) => ({
  approved: C.ok,
  refused:  C.danger,
  partial:  C.warn,
  pending:  C.textSec,
}[status] || C.textSec);

const statusLabel = (status) => ({
  approved: 'Aprovado',
  refused:  'Recusado',
  partial:  'Parcial',
  pending:  'Pendente',
}[status] || status);

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

const inputStyle = {
  background: C.inner, border: `1px solid ${C.border}`, borderRadius: 8,
  color: C.textMain, padding: '8px 10px', fontSize: 13, width: '100%',
  boxSizing: 'border-box', outline: 'none',
};

export default function SeatRequestWidget() {
  const user = useSelector(state => state.user?.data);
  const permission = (user?.permission || '').toLowerCase();
  const isApprover = APPROVER_PERMISSIONS.includes(permission);

  const [open, setOpen]   = useState(false);
  const [tab, setTab]     = useState('request'); // request | mine | approve

  /* ── consultor: busca/carrinho ── */
  const [tours, setTours]           = useState([]);
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState(null);
  const [date, setDate]             = useState(null);          // Date (DatePicker)
  const [tourDates, setTourDates]   = useState([]);            // [{cupo_date, total_cupos}] do tour selecionado
  const [avail, setAvail]           = useState(null); // {available, used, free, blocked, exists}
  const [qty, setQty]               = useState('1');
  const [cart, setCart]             = useState([]);   // [{tour_id, tour_name, tour_date, qty}]
  const [reqComment, setReqComment] = useState('');   // comentário do consultor p/ o aprovador
  const [sending, setSending]       = useState(false);
  const [sentCode, setSentCode]     = useState(null);
  const [error, setError]           = useState('');

  /* ── meus pedidos ── */
  const [mine, setMine] = useState([]);

  /* ── aprovador ── */
  const [pendingList, setPendingList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [expanded, setExpanded]       = useState(null); // request id aberto
  const [editQty, setEditQty]         = useState({});   // {itemId: qty}
  const [comments, setComments]       = useState({});   // {requestId: comment}
  const [acting, setActing]           = useState(false);
  const [historyModal, setHistoryModal] = useState(null); // request aberto no modal de detalhe

  /* ── carga de tours (uma vez, ao abrir) ── */
  useEffect(() => {
    if (!open || tours.length > 0) return;
    apiClient.httpInstance.get('/tours', { params: { pageSize: 500, array: 'true' } })
      .then(res => setTours(res.data?.data ?? res.data ?? []))
      .catch(() => {});
  }, [open, tours.length]);

  /* ── dias com cupos do tour selecionado (mesmo endpoint do orçamento) ── */
  useEffect(() => {
    if (!selected) { setTourDates([]); return; }
    apiClient.httpInstance.get(`reservations_dates/available?tour_id=${selected.id}`)
      .then(res => setTourDates(res.data ?? []))
      .catch(() => setTourDates([]));
  }, [selected]);

  const dateStr = useMemo(() => {
    if (!date) return '';
    try { return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
    catch { return ''; }
  }, [date]);

  /* ── disponibilidade ao escolher tour+data ── */
  useEffect(() => {
    if (!selected || !dateStr) { setAvail(null); return; }
    apiClient.httpInstance.get('/seat_requests/availability', { params: { tour_id: selected.id, date: dateStr } })
      .then(res => setAvail(res.data))
      .catch(() => setAvail(null));
  }, [selected, dateStr]);

  /* ── marcador nos dias que têm cupos (não bloqueia dia fechado — é o que se pede liberar) ── */
  const renderPickerDay = (day, _selectedDate, dayInCurrentMonth, dayComponent) => {
    if (!dayInCurrentMonth || tourDates.length === 0) return dayComponent;
    const ds = new Date(day.getTime() - day.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const info = tourDates.find(d => String(d.cupo_date).slice(0, 10) === ds);
    const hasCupos = info && Number(info.total_cupos) > 0;
    return (
      <div style={{ position: 'relative' }}>
        {dayComponent}
        {hasCupos && (
          <span style={{
            position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)',
            width: 5, height: 5, borderRadius: '50%', background: C.ok, pointerEvents: 'none',
          }} />
        )}
      </div>
    );
  };

  /* ── meus pedidos ── */
  const fetchMine = useCallback(() => {
    apiClient.httpInstance.get('/seat_requests/mine')
      .then(res => setMine(res.data ?? []))
      .catch(() => {});
  }, []);

  /* ── fila de aprovação (poll p/ bolinha amarela) ── */
  const fetchPending = useCallback(() => {
    if (!isApprover) return;
    apiClient.httpInstance.get('/seat_requests/pending')
      .then(res => setPendingList(res.data ?? []))
      .catch(() => {});
  }, [isApprover]);

  /* ── histórico (encerrados) ── */
  const fetchHistory = useCallback(() => {
    if (!isApprover) return;
    apiClient.httpInstance.get('/seat_requests/history')
      .then(res => setHistoryList(res.data ?? []))
      .catch(() => {});
  }, [isApprover]);

  useEffect(() => {
    fetchMine();
    fetchPending();
    const id = setInterval(() => { fetchMine(); fetchPending(); }, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchMine, fetchPending]);

  // carrega histórico ao abrir a aba
  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab, fetchHistory]);

  /* ── abre via clique na notificação do sininho (NotificationBell) ── */
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      setError(''); setSentCode(null);
      setTab(e.detail?.tab === 'mine' ? 'mine' : 'approve');
      fetchMine(); fetchPending();
    };
    window.addEventListener('ft-open-seat-requests', handler);
    return () => window.removeEventListener('ft-open-seat-requests', handler);
  }, [fetchMine, fetchPending]);

  /* ── cor da bola flutuante ── */
  const lastReviewed = useMemo(() => mine.find(r => r.status !== 'pending'), [mine]);
  const hasPendingApprovals = isApprover && pendingList.length > 0;
  const ballColor = hasPendingApprovals
    ? C.warn
    : (lastReviewed ? statusColor(lastReviewed.status) : C.accent);

  /* ── consultor: handlers ── */
  const results = useMemo(() => {
    if (search.length < 2) return [];
    const q = search.toLowerCase();
    return tours.filter(t => (t.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [search, tours]);

  const addToCart = () => {
    const qtyNum = Math.max(1, parseInt(qty) || 1);
    if (!selected || !dateStr || qtyNum < 1) return;
    setError('');
    setCart(prev => {
      const ix = prev.findIndex(i => i.tour_id === selected.id && i.tour_date === dateStr);
      if (ix >= 0) {
        const copy = [...prev];
        copy[ix] = { ...copy[ix], qty: qtyNum };
        return copy;
      }
      return [...prev, { tour_id: selected.id, tour_name: selected.name, tour_date: dateStr, qty: qtyNum }];
    });
    setSelected(null); setSearch(''); setDate(null); setAvail(null); setQty('1'); setTourDates([]);
  };

  const submitRequest = () => {
    if (cart.length === 0) return;
    setSending(true); setError('');
    apiClient.httpInstance.post('/seat_requests', {
      items: cart.map(i => ({ tour_id: i.tour_id, tour_date: i.tour_date, qty: i.qty })),
      consultant_comment: reqComment.trim() || null,
    })
      .then(res => {
        setSentCode(res.data?.code ?? 'LB');
        setCart([]);
        setReqComment('');
        fetchMine();
      })
      .catch(e => setError(e?.response?.data?.message || 'Erro ao enviar o pedido.'))
      .finally(() => setSending(false));
  };

  /* ── aprovador: handlers ── */
  const itemQty = (item) => Math.max(1, parseInt(editQty[item.id] ?? item.requested_qty) || item.requested_qty);

  const approveItem = (req, item) => {
    setActing(true);
    apiClient.httpInstance.put(`/seat_requests/${req.id}/items/${item.id}/approve`, { qty: itemQty(item) })
      .then(() => fetchPendingDetail())
      .catch(e => {
        if (e?.response?.status === 422) {
          fetchPendingDetail(); // item já revisado — atualiza estado
        } else {
          setError(e?.response?.data?.message || 'Erro ao aprovar item.');
        }
      })
      .finally(() => setActing(false));
  };

  const refuseItem = (req, item) => {
    setActing(true);
    apiClient.httpInstance.put(`/seat_requests/${req.id}/items/${item.id}/refuse`)
      .then(() => fetchPendingDetail())
      .catch(e => {
        if (e?.response?.status === 422) {
          fetchPendingDetail(); // item já revisado — atualiza estado
        } else {
          setError(e?.response?.data?.message || 'Erro ao recusar item.');
        }
      })
      .finally(() => setActing(false));
  };

  const approveAll = (req) => {
    setActing(true);
    const items = req.items.filter(i => i.status === 'pending').map(i => ({ id: i.id, qty: itemQty(i) }));
    apiClient.httpInstance.post(`/seat_requests/${req.id}/approve_all`, { items })
      .then(() => fetchPendingDetail())
      .catch(e => setError(e?.response?.data?.message || 'Erro ao aprovar.'))
      .finally(() => setActing(false));
  };

  const finalizeRequest = (req) => {
    setActing(true);
    apiClient.httpInstance.post(`/seat_requests/${req.id}/finalize`, { comment: comments[req.id] || null })
      .then(() => { setExpanded(null); fetchPending(); fetchHistory(); })
      .catch(e => setError(e?.response?.data?.message || 'Erro ao finalizar.'))
      .finally(() => setActing(false));
  };

  const fetchPendingDetail = () => {
    apiClient.httpInstance.get('/seat_requests/pending')
      .then(res => setPendingList(res.data ?? []))
      .catch(() => {});
  };

  /* ════════════════ render ════════════════ */

  return <>
    {/* ── modal detalhe encerrado ── */}
    {historyModal && createPortal(
      <div onClick={() => setHistoryModal(null)} style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          background: '#0f1e35', borderRadius: 14, padding: 24, width: 360, maxWidth: '92vw',
          maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          {/* header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ color: '#62dec2', fontWeight: 700, fontSize: 15 }}>{historyModal.code}</div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                Solicitado por <b style={{ color: '#e2e8f0' }}>{historyModal.user_name}</b>
              </div>
            </div>
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4,
              color: statusColor(historyModal.status), fontSize: 12, fontWeight: 700,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor(historyModal.status), display: 'inline-block' }} />
              {statusLabel(historyModal.status)}
            </span>
          </div>

          {/* itens */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Itens</div>
            {historyModal.items.map(i => (
              <div key={i.id} style={{
                background: '#162032', borderRadius: 8, padding: '8px 12px', marginBottom: 6,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600 }}>{i.tour_name}</div>
                  <div style={{ color: '#94a3b8', fontSize: 11 }}>{fmtDate(i.tour_date)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#94a3b8', fontSize: 11 }}>pedido: {i.requested_qty}</div>
                  <div style={{ color: statusColor(i.status), fontSize: 12, fontWeight: 700 }}>
                    {i.status === 'approved' ? `+${i.approved_qty} liberado` : i.status === 'refused' ? 'recusado' : i.status}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* comentário do consultor */}
          {historyModal.consultant_comment && (
            <div style={{ background: '#162032', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>💬 {historyModal.user_name}</div>
              <div style={{ color: '#e2e8f0', fontSize: 12, fontStyle: 'italic' }}>"{historyModal.consultant_comment}"</div>
            </div>
          )}

          {/* revisão */}
          {historyModal.reviewer_name && (
            <div style={{ background: '#162032', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>✅ Revisado por <b style={{ color: '#e2e8f0' }}>{historyModal.reviewer_name}</b></div>
              {historyModal.comment && (
                <div style={{ color: '#e2e8f0', fontSize: 12, fontStyle: 'italic' }}>"{historyModal.comment}"</div>
              )}
            </div>
          )}

          <button onClick={() => setHistoryModal(null)} style={{
            width: '100%', marginTop: 8, background: '#1e3a5f', border: 'none', borderRadius: 8,
            padding: '10px 0', color: '#94a3b8', fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>Fechar</button>
        </div>
      </div>
    , document.body)}
    {/* ── bola flutuante ── */}
    <div
      onClick={() => { setOpen(o => !o); setError(''); setSentCode(null); if (isApprover && pendingList.length > 0) setTab('approve'); }}
      title="Liberação de cupos"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
        width: 56, height: 56, borderRadius: '50%',
        background: C.card, border: `2px solid ${ballColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
      }}
    >
      <Icon style={{ color: ballColor }}>event_seat</Icon>
      {hasPendingApprovals && (
        <span style={{
          position: 'absolute', top: -4, right: -4, background: C.warn, color: '#0f1e35',
          borderRadius: '50%', minWidth: 20, height: 20, fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
        }}>
          {pendingList.length}
        </span>
      )}
    </div>

    {/* ── janela ── */}
    {open && (
      <div style={{
        position: 'fixed', bottom: 92, right: 24, zIndex: 1300,
        width: 420, maxWidth: 'calc(100vw - 32px)', maxHeight: '72vh',
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
        boxShadow: '0 8px 32px rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* header */}
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: C.textMain, fontWeight: 700, fontSize: 14 }}>Liberação de cupos</span>
          <Icon style={{ color: C.textSec, cursor: 'pointer', fontSize: 18 }} onClick={() => setOpen(false)}>close</Icon>
        </div>

        {/* tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
          {[
            { id: 'request', label: 'Solicitar' },
            { id: 'mine',    label: 'Meus pedidos' },
            ...(isApprover ? [
              { id: 'approve', label: `Aprovações${pendingList.length ? ` (${pendingList.length})` : ''}` },
              { id: 'history', label: 'Encerrados' },
            ] : []),
          ].map(t => (
            <div key={t.id} onClick={() => { setTab(t.id); setError(''); }} style={{
              flex: 1, textAlign: 'center', padding: '10px 4px', cursor: 'pointer', fontSize: 12,
              color: tab === t.id ? C.accent : C.textSec, fontWeight: tab === t.id ? 700 : 400,
              borderBottom: tab === t.id ? `2px solid ${C.accent}` : '2px solid transparent',
            }}>
              {t.label}
            </div>
          ))}
        </div>

        <div style={{ overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {error && <span style={{ color: C.danger, fontSize: 12 }}>{error}</span>}

          {/* ════ aba SOLICITAR ════ */}
          {tab === 'request' && <>
            {sentCode ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Icon style={{ color: C.ok, fontSize: 40 }}>check_circle</Icon>
                <p style={{ color: C.textMain, fontSize: 14, margin: '10px 0 4px' }}>Pedido enviado!</p>
                <p style={{ color: C.accent, fontWeight: 700, fontSize: 18, margin: 0 }}>{sentCode}</p>
                <p style={{ color: C.textSec, fontSize: 12, marginTop: 8 }}>Você será avisado no sininho quando for revisado.</p>
                <button onClick={() => setSentCode(null)} style={{
                  marginTop: 12, background: 'transparent', border: `1px solid ${C.accent}`, color: C.accent,
                  borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13,
                }}>Novo pedido</button>
              </div>
            ) : <>
              <p style={{ color: C.textSec, fontSize: 12, margin: 0 }}>
                Bem-vindo ao sistema de liberação de cupos. Pesquise o passeio, escolha a data e monte seu pedido.
              </p>

              {/* busca tour */}
              <div>
                <input
                  style={inputStyle}
                  placeholder="Pesquisar passeio..."
                  value={selected ? (selected.name ?? '') : search}
                  onChange={e => { setSelected(null); setSearch(e.target.value); }}
                />
                {!selected && results.length > 0 && (
                  <div style={{ background: C.inner, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
                    {results.map(t => (
                      <div key={t.id} onClick={() => { setSelected(t); setSearch(''); }} style={{
                        padding: '8px 12px', cursor: 'pointer', color: C.textMain, fontSize: 13,
                        borderBottom: `1px solid ${C.border}`,
                      }}>
                        {t.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selected && <>
                <div className="seatreq-datepicker">
                  <style>{`
                    .seatreq-datepicker .MuiInputLabel-root { color: ${C.textSec} !important; }
                    .seatreq-datepicker .MuiInputBase-input { color: ${C.textMain} !important; }
                    .seatreq-datepicker .MuiOutlinedInput-notchedOutline { border-color: ${C.border} !important; }
                    .seatreq-datepicker .MuiIconButton-root { color: ${C.textSec} !important; }
                  `}</style>
                  <DatePicker
                    label="Data"
                    value={date}
                    onChange={d => setDate(d)}
                    renderDay={renderPickerDay}
                    disablePast
                  />
                </div>
                {tourDates.length > 0 && (
                  <span style={{ color: C.textSec, fontSize: 10, marginTop: -6 }}>
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: C.ok, marginRight: 4 }} />
                    dias com cupos abertos — dias sem marca podem ser pedidos (a aprovação cria as vagas)
                  </span>
                )}

                {avail && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { label: 'Liberadas', value: avail.available, color: C.textMain },
                      { label: 'Usadas',    value: avail.used,      color: C.warn },
                      { label: 'Livres',    value: avail.free,      color: avail.free > 0 ? C.ok : C.danger },
                    ].map(col => (
                      <div key={col.label} style={{ flex: 1, background: C.inner, borderRadius: 8, padding: '8px 4px', textAlign: 'center' }}>
                        <div style={{ color: C.textSec, fontSize: 10, textTransform: 'uppercase' }}>{col.label}</div>
                        <div style={{ color: col.color, fontWeight: 700, fontSize: 18 }}>{col.value}</div>
                      </div>
                    ))}
                  </div>
                )}
                {avail && !avail.exists && (
                  <span style={{ color: C.warn, fontSize: 11 }}>Dia sem saída aberta — a aprovação criará as vagas.</span>
                )}
                {avail && avail.blocked && (
                  <span style={{ color: C.warn, fontSize: 11 }}>Dia fechado — a aprovação reabre com as vagas liberadas.</span>
                )}

                {date && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number" min={1} style={{ ...inputStyle, width: 90 }}
                      value={qty}
                      onChange={e => setQty(e.target.value)}
                      onBlur={e => setQty(String(Math.max(1, parseInt(e.target.value) || 1)))}
                      onFocus={e => e.target.select()}
                      onClick={e => e.target.select()}
                    />
                    <button onClick={addToCart} style={{
                      flex: 1, background: C.accent, border: 'none', borderRadius: 8, padding: '9px 0',
                      color: '#0f1e35', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}>
                      Adicionar ao pedido
                    </button>
                  </div>
                )}
              </>}

              {/* carrinho */}
              {cart.length > 0 && <>
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                  <span style={{ color: C.textSec, fontSize: 11, textTransform: 'uppercase' }}>Itens do pedido</span>
                  {cart.map((item, ix) => (
                    <div key={`${item.tour_id}-${item.tour_date}`} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: C.textMain, fontSize: 13 }}>{item.tour_name}</div>
                        <div style={{ color: C.textSec, fontSize: 11 }}>{fmtDate(item.tour_date)}</div>
                      </div>
                      <input
                        type="number" min={1}
                        style={{ ...inputStyle, width: 64, padding: '5px 6px' }}
                        value={item.qty}
                        onChange={e => setCart(prev => prev.map((it, i) => i === ix ? { ...it, qty: e.target.value } : it))}
                        onBlur={e => {
                          const v = Math.max(1, parseInt(e.target.value) || 1);
                          setCart(prev => prev.map((it, i) => i === ix ? { ...it, qty: v } : it));
                        }}
                        onFocus={e => e.target.select()}
                        onClick={e => e.target.select()}
                      />
                      <Icon
                        style={{ color: C.danger, fontSize: 18, cursor: 'pointer' }}
                        onClick={() => setCart(prev => prev.filter((_, i) => i !== ix))}
                      >delete</Icon>
                    </div>
                  ))}
                </div>
                <textarea
                  rows={2}
                  placeholder="Quer explicar algo para quem vai liberar? (opcional)"
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  value={reqComment}
                  onChange={e => setReqComment(e.target.value)}
                />
                <button onClick={submitRequest} disabled={sending} style={{
                  background: C.accent, border: 'none', borderRadius: 10, padding: '11px 0',
                  color: '#0f1e35', fontWeight: 700, fontSize: 14, cursor: sending ? 'not-allowed' : 'pointer',
                  opacity: sending ? 0.7 : 1,
                }}>
                  {sending ? 'Enviando...' : 'Pedir liberação'}
                </button>
              </>}
            </>}
          </>}

          {/* ════ aba MEUS PEDIDOS ════ */}
          {tab === 'mine' && (
            mine.length === 0
              ? <span style={{ color: C.textSec, fontSize: 13 }}>Nenhum pedido ainda.</span>
              : mine.map(r => (
                <div key={r.id} style={{ background: C.inner, borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: C.textMain, fontWeight: 700, fontSize: 13 }}>{r.code}</span>
                    <span style={{
                      color: statusColor(r.status), fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor(r.status), display: 'inline-block' }} />
                      {statusLabel(r.status)}
                    </span>
                  </div>
                  {r.consultant_comment && (
                    <div style={{ color: C.textSec, fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>
                      💬 Você: “{r.consultant_comment}”
                    </div>
                  )}
                  {r.items.map(i => (
                    <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12 }}>
                      <span style={{ color: C.textMain }}>{i.tour_name} — {fmtDate(i.tour_date)}</span>
                      <span style={{ color: statusColor(i.status) }}>
                        {i.status === 'approved' ? `+${i.approved_qty}` : i.status === 'refused' ? 'recusado' : `${i.requested_qty} pedido`}
                      </span>
                    </div>
                  ))}
                  {r.comment && (
                    <div style={{ color: C.textSec, fontSize: 11, marginTop: 8, fontStyle: 'italic' }}>
                      ↩️ {r.reviewer_name || 'Aprovador'}: “{r.comment}”
                    </div>
                  )}
                </div>
              ))
          )}

          {/* ════ aba APROVAÇÕES ════ */}
          {tab === 'approve' && isApprover && (
            pendingList.length === 0
              ? <span style={{ color: C.textSec, fontSize: 13 }}>Nenhum pedido pendente.</span>
              : pendingList.map(req => {
                const isOpen = expanded === req.id;
                const pendingItems = req.items.filter(i => i.status === 'pending');
                return (
                  <div key={req.id} style={{ background: C.inner, borderRadius: 10, padding: 12 }}>
                    <div
                      onClick={() => setExpanded(isOpen ? null : req.id)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    >
                      <div>
                        <span style={{ color: C.textMain, fontWeight: 700, fontSize: 13 }}>{req.code}</span>
                        <span style={{ color: C.textSec, fontSize: 12, marginLeft: 8 }}>{req.user_name}</span>
                      </div>
                      <span style={{ color: C.warn, fontSize: 11 }}>{pendingItems.length} pendente{pendingItems.length !== 1 ? 's' : ''}</span>
                    </div>

                    {isOpen && <>
                      {req.consultant_comment && (
                        <div style={{ background: C.card, borderRadius: 8, padding: '8px 10px', marginTop: 8, fontSize: 12, color: C.textMain }}>
                          💬 <span style={{ color: C.textSec }}>{req.user_name}:</span> “{req.consultant_comment}”
                        </div>
                      )}
                      {req.items.map(item => (
                        <div key={item.id} style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 8 }}>
                          <div style={{ color: C.textMain, fontSize: 13, fontWeight: 600 }}>
                            {item.tour_name} — {fmtDate(item.tour_date)}
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 6, fontSize: 11, color: C.textSec, flexWrap: 'wrap' }}>
                            <span>Liberadas: <b style={{ color: C.textMain }}>{item.current?.available ?? item.available_snapshot}</b></span>
                            <span>Livres: <b style={{ color: C.textMain }}>{item.current?.free ?? '-'}</b></span>
                            <span>Pedido: <b style={{ color: C.warn }}>{item.requested_qty}</b></span>
                            <span>Após: <b style={{ color: C.ok }}>{(item.current?.available ?? 0) + itemQty(item)}</b></span>
                          </div>
                          {item.status === 'pending' ? (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                              <input
                                type="number" min={1}
                                style={{ ...inputStyle, width: 64, padding: '5px 6px' }}
                                value={editQty[item.id] ?? item.requested_qty}
                                onChange={e => setEditQty(prev => ({ ...prev, [item.id]: e.target.value }))}
                                onBlur={e => {
                                  const v = Math.max(1, parseInt(e.target.value) || item.requested_qty);
                                  setEditQty(prev => ({ ...prev, [item.id]: v }));
                                }}
                                onFocus={e => e.target.select()}
                                onClick={e => e.target.select()}
                              />
                              <button disabled={acting} onClick={() => approveItem(req, item)} style={{
                                flex: 1, background: C.ok, border: 'none', borderRadius: 8, padding: '7px 0',
                                color: '#0f1e35', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                              }}>Aprovar</button>
                              <button disabled={acting} onClick={() => refuseItem(req, item)} style={{
                                flex: 1, background: 'transparent', border: `1px solid ${C.danger}`, borderRadius: 8,
                                padding: '7px 0', color: C.danger, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                              }}>Recusar</button>
                            </div>
                          ) : (
                            <span style={{ color: statusColor(item.status), fontSize: 12, fontWeight: 700, marginTop: 6, display: 'inline-block' }}>
                              {item.status === 'approved' ? `Aprovado +${item.approved_qty}` : 'Recusado'}
                            </span>
                          )}
                        </div>
                      ))}

                      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 10, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {pendingItems.length > 0 && (
                          <button disabled={acting} onClick={() => approveAll(req)} style={{
                            background: C.accent, border: 'none', borderRadius: 8, padding: '9px 0',
                            color: '#0f1e35', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                          }}>
                            Aprovar todos os pendentes
                          </button>
                        )}
                        <textarea
                          rows={2}
                          placeholder="Comentário para o consultor (opcional)"
                          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                          value={comments[req.id] ?? ''}
                          onChange={e => setComments(prev => ({ ...prev, [req.id]: e.target.value }))}
                        />
                        <button
                          disabled={acting || pendingItems.length > 0}
                          onClick={() => finalizeRequest(req)}
                          title={pendingItems.length > 0 ? 'Aprove ou recuse todos os itens antes de finalizar' : ''}
                          style={{
                            background: pendingItems.length > 0 ? C.border : C.ok,
                            border: 'none', borderRadius: 8, padding: '9px 0',
                            color: pendingItems.length > 0 ? C.textSec : '#0f1e35',
                            fontWeight: 700, fontSize: 13,
                            cursor: pendingItems.length > 0 ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Finalizar e devolver ao consultor
                        </button>
                      </div>
                    </>}
                  </div>
                );
              })
          )}

          {/* ════ aba ENCERRADOS ════ */}
          {tab === 'history' && isApprover && (
            historyList.length === 0
              ? <span style={{ color: C.textSec, fontSize: 13 }}>Nenhum pedido encerrado ainda.</span>
              : historyList.map(req => (
                <div key={req.id} onClick={() => setHistoryModal(req)} style={{ background: C.inner, borderRadius: 10, padding: 12, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ color: C.textMain, fontWeight: 700, fontSize: 13 }}>{req.code}</span>
                      <span style={{ color: C.textSec, fontSize: 12, marginLeft: 8 }}>{req.user_name}</span>
                    </div>
                    <span style={{
                      color: statusColor(req.status), fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor(req.status), display: 'inline-block' }} />
                      {statusLabel(req.status)}
                    </span>
                  </div>
                  {req.reviewer_name && (
                    <div style={{ color: C.textSec, fontSize: 11, marginTop: 4 }}>
                      Revisado por <b style={{ color: C.textMain }}>{req.reviewer_name}</b>
                    </div>
                  )}
                  {req.consultant_comment && (
                    <div style={{ color: C.textSec, fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>
                      💬 {req.user_name}: “{req.consultant_comment}”
                    </div>
                  )}
                  {req.items.map(i => (
                    <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12 }}>
                      <span style={{ color: C.textMain }}>{i.tour_name} — {fmtDate(i.tour_date)}</span>
                      <span style={{ color: statusColor(i.status) }}>
                        {i.status === 'approved' ? `liberado +${i.approved_qty}` : i.status === 'refused' ? 'recusado' : `${i.requested_qty} pedido`}
                      </span>
                    </div>
                  ))}
                  {req.comment && (
                    <div style={{ color: C.textSec, fontSize: 11, marginTop: 8, fontStyle: 'italic' }}>
                      ↩️ {req.reviewer_name || 'Aprovador'}: “{req.comment}”
                    </div>
                  )}
                </div>
              ))
          )}
        </div>
      </div>
    )}
  </>;
}
